import { useState, type CSSProperties, type FormEvent } from "react";
import { useSession } from "../state/session.tsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertIcon, EyeIcon, EyeOffIcon } from "../components/icons.tsx";
import { BucorMark } from "../components/BucorMark.tsx";
import { cn } from "@/lib/utils";
import loginArt from "../assets/login-art.jpg";

/**
 * Экран входа.
 *
 * ── Устройство ──────────────────────────────────────────────────────────────
 *
 * Две колонки: слева фирменная иллюстрация (сгенерирована в Higgsfield —
 * стеклянные плитки, рифма к знаку из четырёх модулей), справа форма.
 * Это единственное место продукта, где уместен парадный язык: внутри
 * рабочих экранов плотность важнее эффекта, и синий там — акцент, а не
 * среда. Здесь наоборот: иллюстрация занимает половину экрана, и синий
 * становится средой на те несколько секунд, пока человек вводит пароль.
 *
 * На узком окне левая колонка превращается в полосу сверху с логотипом:
 * иллюстрация не должна вытеснять форму за пределы экрана телефона.
 *
 * Сообщение об ошибке приходит с сервера и намеренно не различает «нет
 * такого пользователя» и «неверный пароль»: уточнение на клиенте свело бы
 * на нет защиту от перечисления учётных записей.
 */
export function LoginView() {
  const { login, error, loading } = useSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
    } catch {
      // Ошибка уже в состоянии сессии; здесь глотаем осознанно.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
      {/* ── Иллюстрация ─────────────────────────────────────────────────── */}
      <aside className="relative flex min-h-[220px] flex-col justify-between overflow-hidden p-6 text-white lg:min-h-screen lg:p-10">
        <img
          src={loginArt}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-[50%_35%]"
        />
        {/*
          Затемнение только там, где лежит текст: полоска под логотипом
          сверху (у картинки там световой блик) и низ под заголовком.
          Середина остаётся чистой — полное затемнение убило бы иллюстрацию.
        */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-[#0b1f6b]/55 via-transparent via-25% to-[#0b1f6b]/85"
        />

        <div
          className="relative flex items-center gap-2.5"
          style={
            {
              "--mark-unit": "#ffffff",
              "--mark-detached": "rgba(255,255,255,0.55)",
            } as CSSProperties
          }
        >
          <span className="grid size-10 place-items-center rounded-xl bg-white/15 ring-1 ring-white/25 backdrop-blur-md">
            <BucorMark className="size-6" />
          </span>
          <span className="text-[15px] font-semibold tracking-[-0.01em]">
            BUCOR <span className="font-medium text-white/70">WORKSPACE</span>
          </span>
        </div>

        <div className="relative hidden max-w-[26rem] lg:block">
          {/*
            Цвет задан на самом заголовке, а не унаследован: базовое правило
            h2 в theme.css красит его в --text, и на синей иллюстрации он
            выходил чёрным. Один светлый акцент — тот же приём, что и в h1
            справа, только в обратной тональности.
          */}
          <h2 className="text-[34px] font-semibold leading-[1.1] tracking-[-0.9px] text-white">
            Рабочее место для{" "}
            <span className="text-sky-300">продуктивной работы</span> агентства
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-white/75">
            Доступ выдаётся на аккаунт, а не отдаётся вместе с ним: роли,
            маскирование и аудит каждого действия.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2">
            {["Несколько аккаунтов", "Роли и доступы", "Аудит действий", "Быстрые ответы"].map(
              (label) => (
                <li
                  key={label}
                  className="rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[12.5px] font-medium backdrop-blur-md"
                >
                  {label}
                </li>
              ),
            )}
          </ul>
        </div>
      </aside>

      {/* ── Форма ───────────────────────────────────────────────────────── */}
      <main className="relative flex items-center justify-center px-6 py-10 sm:px-10">
        {/* Мягкое синее свечение за формой — тонкая связка с иллюстрацией. */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 size-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/10 blur-3xl dark:bg-brand/15"
        />

        <div className="relative w-full max-w-[400px]">
          <h1 className="text-[34px] font-semibold leading-[1.1] tracking-[-1.1px] text-heading">
            Вход для <span className="text-brand">сотрудников</span>
          </h1>
          <p className="mt-2.5 text-[15px] leading-relaxed text-soft">
            Введите рабочую почту и пароль, выданные администратором.
          </p>

          <form onSubmit={(e) => void submit(e)} className="mt-8 grid gap-5">
            <div className="grid gap-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="name@company.com"
                required
                autoFocus
                className="h-11 rounded-xl px-3.5 md:text-[15px]"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="login-password">Пароль</Label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  required
                  className="h-11 rounded-xl px-3.5 pr-11 md:text-[15px]"
                />
                {/*
                  Показ пароля — кнопка, а не иконка с обработчиком: у неё
                  есть подпись для скринридера и фокус с клавиатуры.
                */}
                <button
                  type="button"
                  aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((v) => !v)}
                  className={cn(
                    "absolute right-1.5 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-lg border-transparent bg-transparent",
                    "text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
                  )}
                >
                  {showPassword ? (
                    <EyeOffIcon className="size-[18px]" />
                  ) : (
                    <EyeIcon className="size-[18px]" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <p
                role="alert"
                className="flex items-center gap-2 rounded-xl bg-destructive/10 px-3.5 py-2.5 text-sm text-destructive"
              >
                <AlertIcon className="size-4 shrink-0" />
                {error}
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              className="mt-1 h-11 w-full rounded-xl text-[15px]"
              disabled={submitting || loading}
            >
              {submitting ? "Вход…" : "Войти"}
            </Button>
          </form>

          <p className="mt-8 text-center text-[13px] leading-relaxed text-muted-foreground">
            Нет доступа? Учётные записи заводит администратор компании.
          </p>
        </div>
      </main>
    </div>
  );
}
